import { QuestionCircleOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Spin } from 'antd';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { envApiUrl } from '../../fetchHooks/envApiUrl.ts';
import {
  type AskAIAnalysisResponse,
  type AskAIAnalysisResponseError,
  fetchAskAIDayAnalysis,
  fetchAskAIHistoricalAnalysis,
} from '../../fetchHooks/useAskAI.ts';
import type { MydayType } from '../../fetchHooks/useMyday.ts';

export const MeasurementsAnalysis = ({ myday }: { myday: MydayType }) => {
  const { t, i18n } = useTranslation();
  const [dayAnalysis, setDayAnalysis] = useState<string>(myday?.dayAnalysis || '');
  const [historicalAnalysis, setHistoricalAnalysis] = useState<string>(myday?.historicalAnalysis || '');
  const queryClient = useQueryClient();
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [speakingText, setSpeakingText] = useState<string>('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Update state when myday prop changes
  useEffect(() => {
    setDayAnalysis(myday?.dayAnalysis || '');
    setHistoricalAnalysis(myday?.historicalAnalysis || '');
  }, [myday?.dayAnalysis, myday?.historicalAnalysis]);

  // Cleanup: stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Convert markdown to plain text (remove markdown syntax)
  const markdownToPlainText = (markdown: string): string => {
    // Remove markdown links but keep the text
    let text = markdown.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    // Remove markdown headers
    text = text.replace(/#{1,6}\s+/g, '');
    // Remove markdown bold/italic
    text = text.replace(/\*\*([^\*]+)\*\*/g, '$1');
    text = text.replace(/\*([^\*]+)\*/g, '$1');
    // Remove markdown code blocks
    text = text.replace(/```[\s\S]*?```/g, '');
    text = text.replace(/`([^`]+)`/g, '$1');
    // Remove markdown lists
    text = text.replace(/^[\*\-\+]\s+/gm, '');
    text = text.replace(/^\d+\.\s+/gm, '');
    // Remove extra whitespace
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.trim();
    return text;
  };

  const handleSpeak = async (text: string) => {
    // Stop any ongoing speech
    if (isSpeaking && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
      return;
    }

    // Convert markdown to plain text
    const plainText = markdownToPlainText(text);

    if (!plainText.trim()) {
      return;
    }

    setIsGeneratingAudio(true);

    try {
      // Get CSRF token
      const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
        withCredentials: true,
      });
      const csrfToken = csrfResponse.data.csrfToken;

      // Call backend TTS endpoint
      const response = await axios.post(
        envApiUrl + '/mydays/text-to-speech',
        {
          text: plainText,
          language: i18n.language === 'nb' ? 'no' : 'en',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
          responseType: 'blob', // Important: receive audio as blob
        },
      );

      // Create audio element and play
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setSpeakingText(text);
        setIsGeneratingAudio(false);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setSpeakingText('');
        URL.revokeObjectURL(audioUrl); // Clean up
        audioRef.current = null;
      };

      audio.onerror = (event) => {
        console.error('Audio playback error:', event);
        setIsSpeaking(false);
        setSpeakingText('');
        setIsGeneratingAudio(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      await audio.play();
    } catch (err: any) {
      console.error('Error generating or playing speech:', err);
      setIsGeneratingAudio(false);

      // If OpenAI TTS is not available, fall back to Web Speech API
      if (err.response?.status === 503 && err.response?.data?.code === 'TTS_NOT_AVAILABLE') {
        console.warn('OpenAI TTS not available, falling back to Web Speech API');
        // Fallback to Web Speech API
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          const plainText = markdownToPlainText(text);
          const utterance = new SpeechSynthesisUtterance(plainText);
          utterance.lang = i18n.language === 'nb' ? 'nb-NO' : 'en-US';

          utterance.onstart = () => {
            setIsSpeaking(true);
            setSpeakingText(text);
          };

          utterance.onend = () => {
            setIsSpeaking(false);
            setSpeakingText('');
          };

          utterance.onerror = () => {
            setIsSpeaking(false);
            setSpeakingText('');
          };

          window.speechSynthesis.speak(utterance);
        }
      }
    }
  };

  const handleStopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const handleAskAIDayAnalysis = async () => {
    const mydayId = myday?.id;
    if (!mydayId) return;

    setDayAnalysis('');
    const isMounted = true;
    try {
      console.log('Started askAIDayAnalysis');
      setDayAnalysis('loading');
      const analysisResult = await fetchAskAIDayAnalysis(mydayId.toString());
      const { data: generatedAnalysis, message, status } = analysisResult as AskAIAnalysisResponse;
      console.log('Ended askAIDayAnalysis, analysis: ', status, message);

      await queryClient.invalidateQueries({ queryKey: ['useMyday', mydayId] });
      if (isMounted) {
        setDayAnalysis(generatedAnalysis);
      }
    } catch (error) {
      if (isMounted) {
        setDayAnalysis(t('myday.aiAnalysisError') + ' ' + (error as AskAIAnalysisResponseError)?.message);
      }
    }
  };

  const handleAskAIHistoricalAnalysis = async () => {
    const mydayId = myday?.id;
    if (!mydayId) return;

    setHistoricalAnalysis('');
    const isMounted = true;
    try {
      console.log('Started askAIHistoricalAnalysis');
      setHistoricalAnalysis('loading');
      const analysisResult = await fetchAskAIHistoricalAnalysis(mydayId.toString());
      const { data: generatedAnalysis, message, status } = analysisResult as AskAIAnalysisResponse;
      console.log('Ended askAIHistoricalAnalysis, analysis: ', status, message);

      await queryClient.invalidateQueries({ queryKey: ['useMyday', mydayId] });
      if (isMounted) {
        setHistoricalAnalysis(generatedAnalysis);
      }
    } catch (error) {
      if (isMounted) {
        setHistoricalAnalysis(t('myday.aiAnalysisError') + ' ' + (error as AskAIAnalysisResponseError)?.message);
      }
    }
  };

  return (
    <div>
      <div className="centered-container">
        {/* Day-specific Analysis */}
        <div style={{ marginBottom: '2rem' }}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}
          >
            <h3 style={{ margin: 0 }}>{t('myday.dayAnalysis')}:</h3>
            {dayAnalysis && dayAnalysis !== 'loading' && (
              <Button
                size="small"
                icon={
                  isGeneratingAudio || (isSpeaking && speakingText === dayAnalysis) ? (
                    <StopOutlined />
                  ) : (
                    <SoundOutlined />
                  )
                }
                onClick={() => {
                  if (isGeneratingAudio || (isSpeaking && speakingText === dayAnalysis)) {
                    handleStopSpeaking();
                  } else {
                    void handleSpeak(dayAnalysis);
                  }
                }}
                loading={isGeneratingAudio && speakingText === dayAnalysis}
                type={isGeneratingAudio || (isSpeaking && speakingText === dayAnalysis) ? 'default' : 'text'}
              >
                {isGeneratingAudio && speakingText === dayAnalysis
                  ? t('myday.generatingAudio') || 'Generating...'
                  : isSpeaking && speakingText === dayAnalysis
                    ? t('myday.stopReading') || 'Stop Reading'
                    : t('myday.readAloud') || 'Read Aloud'}
              </Button>
            )}
          </div>
          {dayAnalysis === 'loading' ? (
            <Spin>{t('myday.waitingForAI')}</Spin>
          ) : dayAnalysis ? (
            <Markdown>{dayAnalysis}</Markdown>
          ) : (
            <Button icon={<QuestionCircleOutlined />} onClick={handleAskAIDayAnalysis}>
              {t('myday.analyzeThisDay')}
            </Button>
          )}
          {dayAnalysis && dayAnalysis !== 'loading' && (
            <Button icon={<QuestionCircleOutlined />} onClick={handleAskAIDayAnalysis} style={{ marginTop: '0.5rem' }}>
              {t('myday.askAIAgain')}
            </Button>
          )}
        </div>

        <Divider />

        {/* Historical Analysis */}
        <div>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}
          >
            <h3 style={{ margin: 0 }}>{t('myday.historicalAnalysis')}:</h3>
            {historicalAnalysis && historicalAnalysis !== 'loading' && (
              <Button
                size="small"
                icon={
                  isGeneratingAudio || (isSpeaking && speakingText === historicalAnalysis) ? (
                    <StopOutlined />
                  ) : (
                    <SoundOutlined />
                  )
                }
                onClick={() => {
                  if (isGeneratingAudio || (isSpeaking && speakingText === historicalAnalysis)) {
                    handleStopSpeaking();
                  } else {
                    void handleSpeak(historicalAnalysis);
                  }
                }}
                loading={isGeneratingAudio && speakingText === historicalAnalysis}
                type={isGeneratingAudio || (isSpeaking && speakingText === historicalAnalysis) ? 'default' : 'text'}
              >
                {isGeneratingAudio && speakingText === historicalAnalysis
                  ? t('myday.generatingAudio') || 'Generating...'
                  : isSpeaking && speakingText === historicalAnalysis
                    ? t('myday.stopReading') || 'Stop Reading'
                    : t('myday.readAloud') || 'Read Aloud'}
              </Button>
            )}
          </div>
          {historicalAnalysis === 'loading' ? (
            <Spin>{t('myday.waitingForAI')}</Spin>
          ) : historicalAnalysis ? (
            <Markdown>{historicalAnalysis}</Markdown>
          ) : (
            <Button icon={<QuestionCircleOutlined />} onClick={handleAskAIHistoricalAnalysis}>
              {t('myday.analyzeWithHistory')}
            </Button>
          )}
          {historicalAnalysis && historicalAnalysis !== 'loading' && (
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={handleAskAIHistoricalAnalysis}
              style={{ marginTop: '0.5rem' }}
            >
              {t('myday.askAIAgain')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
